/**
 * ⚠️ Status: In Development - Adoption Guidelines
 *
 * While you can start adopting these new spacing tokens, please be aware:
 * - Breaking changes may occur as we refine the system
 * - Component-level updates (buttons, inputs, etc.) are pending
 * - Some inconsistencies may exist during transition
 *
 * If adopting now:
 * - Test heavily in your specific use cases
 * - Refer to designs or ask design for intended usage
 * - Expect component-level updates for fixes
 * - Report any issues you encounter
 *
 * Recent changes:
 * - Added new spacing system
 * - Added comprehensive documentation
 * - Component updates pending
 */

/**
 * Spacing system for the P0 application.
 *
 * This system provides a consistent set of spacing values for:
 * - Margins and padding
 * - Border radius
 * - Gaps and gutters
 * - Layout spacing
 *
 * Usage guidelines:
 * - Use these tokens for consistent spacing throughout the application
 * - Follow the scale for different levels of spacing needs
 * - Combine tokens for complex layouts
 */

export const spacing = {
  // Base spacing unit (4px)
  base: 4,

  // Spacing scale (multiples of base)
  scale: {
    xs: 4, // 4px
    sm: 8, // 8px
    md: 12, // 12px
    lg: 16, // 16px
    xl: 24, // 24px
    xxl: 32, // 32px
    xxxl: 48, // 48px
    xxxxl: 64, // 64px
  },

  // Border radius
  radius: {
    none: 0,
    sm: 4, // 4px
    md: 8, // 8px
    lg: 12, // 12px
    xl: 16, // 16px
    full: 9999, // For circular elements
  },

  // Layout spacing
  layout: {
    container: {
      padding: {
        xs: 16, // 16px
        sm: 24, // 24px
        md: 32, // 32px
        lg: 48, // 48px
      },
    },
    section: {
      gap: 32, // 32px
    },
    grid: {
      gap: {
        xs: 8, // 8px
        sm: 16, // 16px
        md: 24, // 24px
        lg: 32, // 32px
      },
    },
  },

  // Component spacing
  components: {
    button: {
      height: 44, // 44px
      padding: {
        vertical: 8, // 8px
        horizontal: 16, // 16px
      },
    },
    input: {
      padding: {
        vertical: 8, // 8px
        horizontal: 12, // 12px
      },
    },
    card: {
      padding: 16, // 16px
    },
  },
};

// Example usage:
// import { spacing } from 'styles/spacing';
//
// styled.div`
//   padding: ${spacing.scale.md}px;
//   margin-bottom: ${spacing.scale.lg}px;
//   border-radius: ${spacing.radius.md}px;
// `;
//
// For Ant Design theme configuration:
// token: {
//   borderRadius: spacing.radius.md,
//   padding: spacing.scale.md,
// }
